import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { ContactForm } from '../form'
import { useCreateContact, useUpdateContact } from '../hooks'
import { contactSchema, type ContactFormData } from '../schema'
import type { Contact } from '@/types/entities'

interface ContactFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    contact: Contact | null
}

export function ContactFormDialog({ open, onOpenChange, contact }: ContactFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!contact
    const createContact = useCreateContact()
    const updateContact = useUpdateContact()

    const form = useForm<ContactFormData>({
        resolver: zodResolver(contactSchema),
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            surname: '',
            email: '',
            phone: '',
            phonePrefix: '',
            position: '',
            team: '',
        },
    })

    useEffect(() => {
        if (open) {
            if (contact) {
                form.reset({
                    firstName: contact.firstName || '',
                    surname: contact.surname || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    phonePrefix: contact.phonePrefix || '',
                    position: contact.position || '',
                    team: contact.team || '',
                })
            } else {
                form.reset({
                    firstName: '',
                    surname: '',
                    email: '',
                    phone: '',
                    phonePrefix: '',
                    position: '',
                    team: '',
                })
            }
        }
    }, [open, contact, form])

    const handleSubmit = async (data: ContactFormData) => {
        try {
            if (isEditing && contact) {
                await updateContact.mutateAsync({
                    id: contact.id,
                    data,
                })
            } else {
                await createContact.mutateAsync(data)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    const isLoading = createContact.isPending || updateContact.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? t('contacts.editContact') : t('contacts.createContact')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('contacts.updateContactInfo')
                            : t('contacts.createNewContactDescription')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                        <ContactForm form={form} isEditing={isEditing} />

                        <div className="flex justify-end space-x-2 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? isEditing
                                        ? t('contacts.updating')
                                        : t('contacts.creating')
                                    : isEditing
                                        ? t('contacts.update')
                                        : t('contacts.create')}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
